import { AlertTriangle } from 'lucide-react';
import { formatCurrency, formatPercent, formatCount } from '@/lib/format';

export type KpiFormat = 'currency' | 'percent' | 'count';

export type KpiDelta = {
  /** 比較対象の実値（前月・前年など。null は「比較不可」表示に） */
  prevValue: number | null;
  /** 比較対象ラベル（例: '前月比', '前年比'） */
  label: string;
  /** true の場合、値が「下がった」ほうを緑（原価率など） */
  invert?: boolean;
};

export type KpiCardProps = {
  title: string;
  value: number | null;
  format: KpiFormat;
  unit?: string;
  subtitle?: string;
  alertMessage?: string;
  delta?: KpiDelta | null;
  /** v0.3 (来客数の汎用化): 算出ソースを小さく表示するバッジ (例: '手入力' / 'メニュー算出' / '混在')。
   *  設計: docs/design/v03-visitor-count-generalization.md §5-4 (算出方式が複数存在しうるため、
   *  数値だけでは「なぜ変わったか」が伝わらない問題への対応、v1スコープに前倒し) */
  sourceBadge?: string;
  /** v0.4 追加要件⑭: カード末尾に**常に**表示する補足行 (例: '目標比 95%')。
   *
   *  ⚠️ `subtitle` は `!alert && !delta` のときしか描画されない。呼び出し元の多くは
   *  前月比 (`delta`) を渡すため、subtitle に補足を入れると**一度も表示されない**
   *  (design-expert が実際に検出。§8-122 型の「実装したが発火しない」)。
   *  出したい情報が delta / alert と共存しうるならこちらを使うこと。 */
  footnote?: string;
};

/** 数値変化を絶対差／相対％で計算 */
const computeDelta = (curr: number | null, prev: number | null) => {
  if (curr == null || prev == null || !Number.isFinite(curr) || !Number.isFinite(prev)) {
    return null;
  }
  const abs = curr - prev;
  const rate = prev === 0 ? null : abs / Math.abs(prev);
  return { abs, rate };
};

/** delta の表示テキスト（値の差、金額は省略しない §4.2） */
const formatDeltaValue = (abs: number, format: KpiFormat, unit?: string): string => {
  const sign = abs > 0 ? '+' : abs < 0 ? '−' : '±';
  const absVal = Math.abs(abs);
  if (format === 'currency') return `${sign}¥${absVal.toLocaleString('ja-JP')}`;
  if (format === 'percent') return `${sign}${(absVal * 100).toFixed(1)}pt`;
  return `${sign}${absVal.toLocaleString('ja-JP')}${unit ?? ''}`;
};

// §7.3 KPIカード: L1、radius.lg、padding 16px
// ラベル(12px/text.sub) → 数値(28px/800/tabular) → 差分バッジ+実数 の3段固定
// 閾値超過は左端4pxの brand.orange バー + alert.* バッジ (カード全体を赤く塗らない)
export function KpiCard({ title, value, format, unit, subtitle, alertMessage, delta, sourceBadge, footnote,
}: KpiCardProps) {
  const displayValue =
    format === 'currency' ? formatCurrency(value)
    : format === 'percent' ? formatPercent(value)
    : formatCount(value);
  const displayUnit = format === 'count' ? (unit?.trim() ?? '') : '';

  const alert = Boolean(alertMessage);
  const deltaData = delta ? computeDelta(value, delta.prevValue) : null;

  // 「良い変化」を判定：invert=true（原価率など）は下方向が良
  const isGood = deltaData == null ? null
    : deltaData.abs === 0 ? null
    : delta?.invert ? deltaData.abs < 0
    : deltaData.abs > 0;

  // §4.2 増減記号は文字+色+数値の三重 (WCAG 1.4.1)
  const deltaArrow = deltaData == null || deltaData.abs === 0 ? '±'
    : deltaData.abs > 0 ? '▲'
    : '▼';

  // バッジの配色: 良い=success / 悪い=danger / 中立=sunken
  const badgeCls = isGood == null ? 'bg-surface-sunken text-text-sub'
    : isGood ? 'bg-success-bg text-success-text'
    : 'bg-danger-bg text-danger-text';

  return (
    <div
      className={`relative overflow-hidden bg-surface-card rounded-lg shadow-card min-h-[128px] p-4 flex flex-col justify-between`}
    >
      {/* 閾値超過: 左端4pxのオレンジバー (§7.3) */}
      {alert && (
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-brand-orange" aria-hidden />
      )}
      {/* 1段目: タイトル (12px, text.sub) + 算出ソースバッジ (来客数カード等、v0.3) */}
      <div className="flex items-center justify-between gap-1">
        <span className="text-[12px] font-bold text-text-sub tracking-wide truncate" title={title}>{title}</span>
        {sourceBadge && (
          <span className="shrink-0 text-[10px] font-semibold text-text-sub bg-surface-sunken rounded-full px-1.5 py-0.5 whitespace-nowrap">
            {sourceBadge}
          </span>
        )}
      </div>
      {/* 2段目: 数値 (kpi-value: 28px/800/tabular/-.025em) — 金額は省略しない (§4.2) */}
      <div className="flex items-baseline gap-1 tabular-nums text-text-main font-extrabold leading-tight overflow-hidden -tracking-[0.025em] mt-1">
        <span
          className="truncate"
          style={{ fontSize: 'clamp(1.375rem, 5.2vw, 1.75rem)' }}
          title={typeof value === 'number' ? String(value) : undefined}
        >
          {displayValue}
        </span>
        {displayUnit && (
          <span className="shrink-0 text-sm font-semibold text-text-sub whitespace-nowrap">
            {displayUnit}
          </span>
        )}
      </div>
      {/* 3段目: 差分バッジ + 実数 (§4.2 2段表示、率+実数) */}
      {delta && (
        <div className="mt-2 flex items-center flex-wrap gap-x-2 gap-y-1"
          aria-label={deltaData
            ? `${delta.label} ${deltaData.abs > 0 ? '増加' : deltaData.abs < 0 ? '減少' : '変化なし'}`
            : `${delta.label} 比較不可`}>
          {deltaData ? (
            <>
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] font-extrabold tabular-nums ${badgeCls}`}>
                <span aria-hidden>{deltaArrow}</span>
                {deltaData.rate != null ? (
                  <span>{(Math.abs(deltaData.rate) * 100).toFixed(1)}{format === 'percent' ? 'pt' : '%'}</span>
                ) : (
                  <span>{formatDeltaValue(deltaData.abs, format, unit)}</span>
                )}
              </span>
              <span className="text-[11px] tabular-nums text-text-sub whitespace-nowrap">
                {formatDeltaValue(deltaData.abs, format, unit)} / {delta.label}
              </span>
            </>
          ) : (
            <span className="text-[11px] text-text-muted">{delta.label} 比較不可</span>
          )}
        </div>
      )}
      {/* 閾値超過メッセージ: alert.* バッジ (§7.3) */}
      {alert && (
        <div className="mt-2 inline-flex self-start items-center gap-1 px-2 py-0.5 rounded-sm bg-alert-bg text-alert-text text-[11px] font-extrabold border border-alert-border/40">
          <AlertTriangle size={12} className="shrink-0" aria-hidden />
          <span>{alertMessage}</span>
        </div>
      )}
      {/* サブタイトル (alert なし、delta なしのケースのみ) */}
      {subtitle && !alert && !delta && (
        <div className="mt-1 text-[11px] text-text-sub">{subtitle}</div>
      )}
      {/* 補足行: delta / alert と共存させたい情報 (⑭ の「目標比」など)。常に描画する */}
      {footnote && (
        <div className="mt-1 text-[11px] text-text-sub">{footnote}</div>
      )}
    </div>
  );
}
